import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/cn';

let _setState = null;

export function confirm(options) {
  return new Promise((resolve) => {
    _setState?.({ ...options, resolve });
  });
}

export function ConfirmDialogProvider() {
  const [state, setState] = useState(null);
  _setState = setState;

  useEffect(() => {
    if (!state) return;
    const handler = (e) => {
      if (e.key === 'Escape') { state.resolve(false); setState(null); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [state]);

  function handleConfirm() { state?.resolve(true);  setState(null); }
  function handleCancel()  { state?.resolve(false); setState(null); }

  if (!state) return null;

  return createPortal(
    <>
      <style>{`
        @keyframes dialog-overlay-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes dialog-panel-in   { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
      `}</style>
      <div className="admin-scope dark font-admin fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div
          className="absolute inset-0 bg-black/70"
          style={{ animation: 'dialog-overlay-in 180ms ease forwards' }}
          onClick={handleCancel}
        />
        <div
          className="relative w-full max-w-sm rounded-xl border border-border bg-card shadow-elev-3"
          style={{ animation: 'dialog-panel-in 200ms cubic-bezier(0.2,0.8,0.2,1) forwards' }}
        >
          {/* Header */}
          <div className="border-b border-border px-5 py-4">
            <h2 className="text-base font-semibold text-foreground">{state.title}</h2>
          </div>

          {/* Body */}
          <div className="px-5 py-4">
            {state.description && (
              <p className="text-sm leading-relaxed text-muted-foreground">{state.description}</p>
            )}

            {state.rows && (
              <div className="overflow-hidden rounded-lg border border-border divide-y divide-border">
                {state.rows.map((row, i) => (
                  <div key={i} className="flex items-center justify-between px-3.5 py-2.5">
                    <span className="text-xs text-muted-foreground">{row.label}</span>
                    <span className={cn(
                      'text-sm font-semibold tabular-nums',
                      row.status === 'warn' ? 'text-amber-400' :
                      row.status === 'error' ? 'text-rose-400' :
                      'text-foreground'
                    )}>
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Footer */}
            <div className={cn('flex justify-end gap-2', (state.description || state.rows) && 'mt-4')}>
              {state.cancelLabel !== null && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex h-8 items-center rounded-lg bg-white/[0.04] px-3 text-xs text-muted-foreground transition-colors hover:bg-white/[0.08] hover:text-foreground"
                >
                  {state.cancelLabel ?? '취소'}
                </button>
              )}
              <button
                type="button"
                onClick={handleConfirm}
                className={cn(
                  'flex h-8 items-center rounded-lg px-3 text-xs font-semibold transition-colors',
                  state.destructive
                    ? 'bg-rose-500/15 text-rose-400 hover:bg-rose-500/25'
                    : 'bg-primary/10 text-primary hover:bg-primary/20'
                )}
              >
                {state.confirmLabel ?? '확인'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

export default ConfirmDialogProvider;
